import React, { useEffect, useRef, useState } from "react";
import { Download, Maximize2, BookOpenText, Upload } from "lucide-react";
import { toast } from "sonner";
import SectionTitle from "../components/site/SectionTitle";

// Default location for the book PDF — owner can replace this file at /app/frontend/public/book/hazrat-book.pdf
// Or upload via the hidden /admin route below.
const BOOK_PDF_PATH = "/book/hazrat-book.pdf";

export default function TheBook() {
  useEffect(() => { document.title = "Our Hazrat's Book — Hazrat Sufi Sarkar"; }, []);
  const iframeRef = useRef(null);
  const [pdfExists, setPdfExists] = useState(true);
  const [pdfKey, setPdfKey] = useState(0);

  useEffect(() => {
    fetch(BOOK_PDF_PATH, { method: "HEAD" })
      .then(r => setPdfExists(r.ok))
      .catch(() => setPdfExists(false));
  }, [pdfKey]);

  const openFullscreen = () => {
    const el = iframeRef.current;
    if (!el) return;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
  };

  return (
    <div data-testid="book-pdf-page">
      <section className="pattern-emerald text-ivory pt-32 pb-24" data-testid="thebook-hero">
        <div className="container-page text-center">
          <span className="text-[11px] uppercase tracking-[0.32em] text-gold">The Book</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-tight">Our Hazrat's Book</h1>
          <p className="text-ivory/80 mt-5 max-w-3xl mx-auto leading-relaxed">
            Explore the written teachings of Hazrat Shahanshah Sufi Sarkar — reflections on divine love, faith, Faqr, and the seeker's path to Allah. Read online or download the complete book below.
          </p>
        </div>
      </section>

      <section className="section-pad" data-testid="thebook-cover-section">
        <div className="container-page grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-4 reveal">
            <div className="mihrab-frame aspect-[3/4] overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_divine-solace/artifacts/zrrdn3hx_WhatsApp%20Image%202026-05-28%20at%2021.42.25.jpeg"
                alt="Hazrat Shahanshah Sufi Sarkar with the Holy Quran"
                className="h-full w-full object-cover"
              />
            </div>
          </div>

          <div className="md:col-span-8 reveal">
            <span className="text-[11px] uppercase tracking-[0.32em] text-gold">Featured Teachings</span>
            <h2 className="font-serif text-3xl md:text-4xl text-emerald mt-4 leading-tight">The written light of Sultan-Ul-Faqr</h2>
            <p className="text-charcoal-muted mt-6 leading-relaxed text-lg">
              Step into the saint's written teachings — verses on devotion, the inner stations of the soul, and the practice of remembrance. Each page a gentle lantern for the seeker.
            </p>

            <div className="flex flex-wrap items-center gap-4 mt-8">
              <a
                href={pdfExists ? BOOK_PDF_PATH : "#"}
                onClick={(e) => { if (!pdfExists) { e.preventDefault(); toast.message("The book will be available here soon."); } }}
                target="_blank"
                rel="noreferrer"
                data-testid="read-online-btn"
                className="btn-gold"
              >
                <BookOpenText size={16} className="mr-2" /> Read Online
              </a>
              <a
                href={pdfExists ? BOOK_PDF_PATH : "#"}
                download="hazrat-sufi-sarkar-book.pdf"
                onClick={(e) => { if (!pdfExists) { e.preventDefault(); toast.message("PDF not yet uploaded."); } }}
                data-testid="download-pdf-btn"
                className="btn-outline-gold"
              >
                <Download size={16} className="mr-2" /> Download PDF
              </a>
              {pdfExists && (
                <button onClick={openFullscreen} data-testid="fullscreen-btn" className="text-charcoal-muted hover:text-emerald inline-flex items-center text-sm">
                  <Maximize2 size={16} className="mr-2" /> Fullscreen
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad pt-0" data-testid="thebook-pdf-section">
        <div className="container-page">
          <div className="rounded-lg overflow-hidden border-2 border-gold/30 shadow-2xl bg-ivory-alt" style={{ height: "85vh", minHeight: 600 }}>
            {pdfExists ? (
              <iframe
                key={pdfKey}
                ref={iframeRef}
                title="Hazrat Sufi Sarkar's Book"
                src={`https://docs.google.com/gview?url=${encodeURIComponent(window.location.origin + BOOK_PDF_PATH)}&embedded=true`}
                className="w-full h-full bg-ivory-alt"
                data-testid="book-pdf-iframe"
              />
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center px-8" data-testid="book-pdf-empty">
                <BookOpenText size={56} className="text-gold mb-6" />
                <h3 className="font-serif text-3xl text-ivory">The book will be available here soon</h3>
                <p className="text-ivory/70 mt-4 max-w-md">
                  Once Hazrat Sufi Sarkar's book is uploaded, it will appear here as an embedded reader with page navigation, zoom, and download.
                </p>
                <p className="text-gold text-sm mt-6">Owner: visit <code className="bg-emerald-deep/40 px-2 py-1 rounded">/admin/book</code> to upload, or place the file at <code className="bg-emerald-deep/40 px-2 py-1 rounded">/book/hazrat-book.pdf</code>.</p>
              </div>
            )}
          </div>

          <p className="text-center text-charcoal-muted text-xs mt-6">
            <Upload size={12} className="inline mr-1" />
            To replace the book, upload your new PDF via the admin route or replace <code>/app/frontend/public/book/hazrat-book.pdf</code>. The page always loads the latest file.
          </p>
        </div>
      </section>
    </div>
  );
}
